/* sugar:assets/subscriptions.js@2.0.0 */
/*
 * Subscriptions — the Subscribe & Save selector and the Subscription Toggle.
 *
 * One controller per `[data-sugar-subscriptions]` root. Everything the rest of the theme
 * relies on stays as it was:
 *   - window.subscriptionStateBySection[sectionId] and window.subscriptionState
 *   - event subscription:type-changed { sectionId, type, sellingPlan, sellingPlanId,
 *     sellingPlanGroupName, variantPrice, variantComparePrice, quantity, compareBasis }
 *   - the hidden input[name="selling_plan"] on the section's product form
 *   - listens: variant:change, offers:quantity-changed, offers:prices-updated,
 *     offers:lock-selling-plan, offers:ready
 *   - root.sugarSubscriptions exposes the controller (selectType, selectPlan, state)
 *
 * The markup is rendered with the first plan's numbers already filled in. Every element that
 * shows a live value keeps its raw text in data-sb-tpl and is refilled here whenever the plan,
 * the variant, the quantity or a selected offer changes. Plan prices come from Shopify's own
 * allocation for each variant (embedded by Liquid), so they match the checkout to the cent.
 */
(function () {
  if (window.SugarSubscriptions) return;

  /*
   * The selected card draws its border in from opposite corners. The class has to come back off
   * when it lands: the wedge that reveals the border leaves a hairline seam where its two ends
   * meet, and the settled frame is drawn without it. Timed rather than driven by animationend,
   * which a backgrounded tab does not reliably dispatch — mirrors the 680ms in the stylesheet.
   */
  const BORDER_TRACE_MS = 740;

  function stopTrace(el) {
    if (el._traceTimer) {
      clearTimeout(el._traceTimer);
      el._traceTimer = null;
    }
    el.classList.remove('is-border-tracing');
  }

  function traceBorder(el) {
    if (!el) return;
    stopTrace(el);
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    void el.offsetWidth;
    el.classList.add('is-border-tracing');
    el._traceTimer = setTimeout(() => {
      el._traceTimer = null;
      el.classList.remove('is-border-tracing');
    }, BORDER_TRACE_MS);
  }

  const ROOT = '[data-sugar-subscriptions]';
  const UNITS = ['day', 'days', 'week', 'weeks', 'month', 'months', 'year', 'years'];

  function toCents(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return 0;
      if (/^-?\d+$/.test(trimmed)) return parseInt(trimmed, 10);
      const normalized = trimmed.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
      const parsed = Number.parseFloat(normalized);
      if (!Number.isFinite(parsed)) return 0;
      return normalized.includes('.') ? Math.round(parsed * 100) : Math.round(parsed);
    }
    return 0;
  }

  function sumCents(values) {
    return values.reduce((sum, value) => sum + toCents(value), 0);
  }

  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try { return JSON.parse(el.textContent || el.getAttribute('content') || ''); } catch (e) { return fallback; }
  }

  function stripHtml(html) {
    return String(html || '').replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
  }

  /* The delivery frequency words from a plan's option value ("2 Months" → 2 / Months) */
  function frequencyOf(plan) {
    let number = '1';
    let unit = '';
    (plan && plan.options ? plan.options : []).forEach((option) => {
      String(option.value || '').split(' ').forEach((part) => {
        if (UNITS.includes(part.toLowerCase())) unit = part;
        else if (/^\d+$/.test(part) && part !== '0') number = part;
      });
    });
    return { number, unit };
  }

  function adjustmentOf(plan) {
    return plan && plan.price_adjustments && plan.price_adjustments[0] ? plan.price_adjustments[0] : null;
  }

  function applyAdjustment(base, plan) {
    const adj = adjustmentOf(plan);
    if (!adj || !base) return base;
    if (adj.value_type === 'percentage') return Math.round(base * (100 - adj.value) / 100);
    if (adj.value_type === 'fixed_amount') return Math.max(0, base - adj.value);
    if (adj.value_type === 'price') return Math.max(0, adj.value);
    return base;
  }

  class Subscriptions {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || (root.closest('.shopify-section') || {}).id?.replace('shopify-section-', '') || null;
      this.layout = root.dataset.sbLayout || 'vertical';
      this.isToggle = this.layout === 'toggle';
      this.requiresPlan = root.hasAttribute('data-sb-requires-plan');
      this.compareBasis = root.dataset.sbCompareBasis || 'onetime';
      this.currency = root.dataset.sbCurrency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      this.showCode = root.dataset.sbCurrencyCode === 'true';
      this.groupName = root.dataset.sbGroupName || '';
      this.optionsFormat = root.dataset.sbOptionsFormat || '[number] [unit]';
      this.discountLabel = root.dataset.sbDiscountText || '';
      this.plans = parseJSON(root.querySelector('[data-sb-plans]'), []);
      this.variants = parseJSON(root.querySelector('[data-sb-variants]'), []);
      this.variantById = new Map(this.variants.map((v) => [String(v.id), v]));
      this.variantId = String(root.dataset.currentVariantId || (this.variants[0] ? this.variants[0].id : ''));
      const variant = this.variantById.get(this.variantId);
      this.variantPrice = variant ? toCents(variant.price) : 0;
      this.variantCompare = variant ? toCents(variant.compare_at_price) : 0;
      this.type = root.dataset.sbPreselected === 'onetime' ? 'onetime' : 'subscribe';
      if (this.requiresPlan) this.type = 'subscribe';
      this.planId = this.plans[0] ? Number(this.plans[0].id) : null;
      this.quantity = 1;
      this.offer = { type: null, value: 0, scope: 'total' };
      this.locked = false;
      this.listeners = [];
      this.autoAlign = (badge) => (this.layout === 'horizontal' ? 'center' : (badge.dataset.obPos === 'top' ? 'right' : 'left'));
    }

    init() {
      this.readSelectedOffer();
      this.readQuantityInput();
      this.bind();
      // A preselected offer or variant can already point at a plan the variant can't take.
      this.resolvePlanForVariant(this.variantId);
      this.syncFrequencyOptions();
      this.syncForm();
      this.render();
      this.publish();
      this.root.dispatchEvent(new CustomEvent('subscriptions:ready', { bubbles: true }));
    }

    get plan() {
      return this.plans.find((p) => Number(p.id) === Number(this.planId)) || null;
    }

    get section() {
      return this.root.closest('.shopify-section');
    }

    listen(target, event, handler, options) {
      target.addEventListener(event, handler, options);
      this.listeners.push(() => target.removeEventListener(event, handler, options));
    }

    destroy() {
      this.listeners.forEach((off) => off());
      this.listeners = [];
      delete this.root.sugarSubscriptions;
    }

    /* === inputs from the rest of the page === */

    selectedOffer() {
      const section = this.section;
      return section ? section.querySelector('.offer.is-selected') : null;
    }

    readSelectedOffer() {
      const offer = this.selectedOffer();
      if (!offer) return false;
      this.quantity = parseInt(offer.dataset.quantity, 10) || this.quantity || 1;
      this.offer = {
        type: offer.dataset.offerDiscountType || null,
        value: parseFloat(offer.dataset.offerDiscountValue) || 0,
        scope: offer.dataset.offerDiscountScope || 'total'
      };
      const lockedId = parseInt(offer.dataset.lockedSellingPlanId || '', 10);
      if (Number.isFinite(lockedId) && lockedId > 0) this.lockToPlan(lockedId, true);
      return true;
    }

    readQuantityInput() {
      if (this.selectedOffer()) return;
      const section = this.section;
      const input = section && section.querySelector('[name="quantity"]');
      const value = input ? parseInt(input.value, 10) : NaN;
      if (Number.isFinite(value) && value > 0) this.quantity = value;
    }

    bind() {
      const root = this.root;

      this.listen(root, 'click', (e) => {
        // Upsell rows toggle their checkbox and never change the selection
        const upsell = e.target.closest('.sugar-offer-gift[data-gift-type="upsell"]');
        if (upsell && root.contains(upsell)) {
          e.stopPropagation();
          this.toggleUpsell(upsell);
          return;
        }
        const pill = e.target.closest('.sugar-subs__pill');
        if (pill && root.contains(pill)) {
          e.preventDefault();
          e.stopPropagation();
          this.selectPlan(pill.dataset.sbPlan);
          return;
        }
        // Native controls inside the card keep their own behaviour
        if (e.target.closest('select, a, button, input, label')) return;
        const option = e.target.closest('.sugar-subs__option');
        if (option && root.contains(option)) this.selectType(option.dataset.sbType);
      });

      this.listen(root, 'keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const upsell = e.target.closest('.sugar-offer-gift[data-gift-type="upsell"]');
        if (upsell && e.target === upsell) {
          e.preventDefault();
          e.stopPropagation();
          this.toggleUpsell(upsell);
          return;
        }
        const option = e.target.closest('.sugar-subs__option');
        if (option && e.target === option) {
          e.preventDefault();
          this.selectType(option.dataset.sbType);
        }
      });

      this.listen(root, 'change', (e) => {
        const dropdown = e.target.closest('.sugar-subs__dropdown');
        if (dropdown) { this.selectPlan(dropdown.value); return; }
        const radio = e.target.closest('.sugar-subs__radio-input');
        if (radio && radio.checked) this.selectPlan(radio.value);
      });

      const sameSection = (e) => !(e.detail && e.detail.sectionId && this.sectionId && String(e.detail.sectionId) !== String(this.sectionId));

      this.listen(document, 'variant:change', (e) => {
        if (!sameSection(e) || !e.detail || !e.detail.variant) return;
        const id = String(e.detail.variant.id);
        if (this.variantById.size && !this.variantById.has(id)) return;
        this.variantId = id;
        this.variantPrice = toCents(e.detail.variant.price);
        this.variantCompare = toCents(e.detail.variant.compare_at_price);
        // Before the price refresh, so the plan on show and the plan the form posts agree.
        this.resolvePlanForVariant(id);
        this.syncFrequencyOptions();
        this.syncForm();
        this.render();
        this.publish();
      });

      this.listen(document, 'offers:quantity-changed', (e) => {
        if (!sameSection(e) || !e.detail || !e.detail.quantity) return;
        const section = this.section;
        const scoped = e.detail.blockId && section ? section.querySelector(`.offer[data-block-id="${e.detail.blockId}"]`) : null;
        if (!e.detail.sectionId && e.detail.blockId && !scoped) return;
        this.quantity = parseInt(e.detail.quantity, 10) || 1;
        if (scoped) {
          this.offer = {
            type: scoped.dataset.offerDiscountType || null,
            value: parseFloat(scoped.dataset.offerDiscountValue) || 0,
            scope: scoped.dataset.offerDiscountScope || 'total'
          };
        }
        this.render();
        this.publish();
      });

      this.listen(document, 'offers:prices-updated', (e) => {
        if (!sameSection(e)) return;
        const offer = this.selectedOffer();
        if (!offer) return;
        const eventBlock = String((e.detail && e.detail.blockId) || '').trim();
        const selectedBlock = String(offer.dataset.blockId || '').trim();
        if (eventBlock && selectedBlock && eventBlock !== selectedBlock) return;
        this.readSelectedOffer();
        this.render();
      });

      this.listen(document, 'offers:lock-selling-plan', (e) => {
        if (!sameSection(e)) return;
        const planId = parseInt(e.detail && e.detail.sellingPlanId, 10);
        if (Number.isFinite(planId) && planId > 0) this.lockToPlan(planId);
        else this.releaseLock();
      });

      // The offers controller picks its initial tier after this script runs
      this.listen(document, 'offers:ready', (e) => {
        if (!sameSection(e)) return;
        if (this.readSelectedOffer()) { this.render(); this.publish(); }
      });

      const section = this.section;
      const quantityInput = section && section.querySelector('input[name="quantity"], select[name="quantity"]');
      if (quantityInput) {
        this.listen(quantityInput, 'change', () => {
          if (this.selectedOffer()) return;
          this.quantity = parseInt(quantityInput.value, 10) || 1;
          this.render();
          this.publish();
        });
      }
    }

    /* === selection === */

    selectType(type) {
      if (this.isToggle) type = this.type === 'subscribe' ? 'onetime' : 'subscribe';
      if (type !== 'subscribe' && type !== 'onetime') return;
      if (this.requiresPlan && type === 'onetime') return;
      this.type = type;
      this.syncForm();
      this.render();
      this.publish();
    }

    selectPlan(id) {
      const numericId = parseInt(id, 10);
      if (!Number.isFinite(numericId) || !this.plans.some((p) => Number(p.id) === numericId)) return;
      this.planId = numericId;
      this.syncForm();
      this.render();
      this.publish();
    }

    // Pin which plan a subscription uses and hide the picker while a selected offer dictates
    // it. This decides the plan, not whether the shopper subscribes: the purchase type stays
    // whatever the merchant preselected or the shopper picked, otherwise switching tiers
    // would silently move a one-time buyer onto a subscription.
    lockToPlan(id, silent) {
      let numericId = parseInt(id, 10);
      if (!Number.isFinite(numericId) || !this.plans.some((p) => Number(p.id) === numericId)) return;
      // A plan the variant can't take would be rejected at add to cart. When the variant has
      // exactly one plan, that is the only sensible lock.
      const allowed = this.plansForVariant(this.variantId);
      if (!allowed.includes(numericId) && allowed.length === 1) numericId = allowed[0];
      this.locked = true;
      this.planId = numericId;
      if (silent) return;
      this.syncForm();
      this.render();
      this.publish();
    }

    releaseLock() {
      if (!this.locked) return;
      this.locked = false;
      this.render();
    }

    toggleUpsell(upsell) {
      const checked = !upsell.classList.contains('is-checked');
      upsell.classList.toggle('is-checked', checked);
      upsell.setAttribute('aria-checked', String(checked));
    }

    /* === the product form === */

    findForm() {
      const section = this.section;
      const find = (scope) =>
        scope &&
        (scope.querySelector('form[data-type="add-to-cart-form"]') ||
          scope.querySelector('product-form form') ||
          scope.querySelector('form[action*="/cart/add"]'));

      /*
       * Nearest form first. A section can hold more than one buy button — the Custom Sticky's
       * desktop and mobile bars each carry their own, under the same form id — and a toggle
       * belongs to the button beside it, not to whichever form the section renders first.
       * Taking the first one wrote the plan onto the other bar's form, so the shopper saw a
       * subscription price and added a one-time purchase.
       */
      const stop = section ? section.parentElement : null;
      for (let el = this.root.parentElement; el && el !== stop; el = el.parentElement) {
        const near = find(el);
        if (near) return near;
      }
      return find(section) || find(document);
    }

    syncForm() {
      const form = this.findForm();
      if (!form) return;
      let input = form.querySelector('input[name="selling_plan"]');
      if (this.type === 'subscribe' && this.planId) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          form.appendChild(input);
        }
        input.value = String(this.planId);
        input.disabled = false;
      } else if (input) {
        input.disabled = true;
        input.value = '';
      }
      if (!form._subscriptionListenerAttached) {
        form._subscriptionListenerAttached = true;
        form.addEventListener('submit', () => this.syncForm(), { capture: true });
      }
    }

    /* === plans per variant === */

    /*
     * The plan ids Shopify will accept on this variant: the ones that carry a price for it.
     * Falls back to every plan when no allocation reached the page, so a store that never
     * hits per-variant plans behaves exactly as before.
     */
    plansForVariant(variantId) {
      const all = this.plans.map((p) => Number(p.id));
      const anyPriced = this.plans.some((p) => p.prices && Object.keys(p.prices).length > 0);
      if (variantId == null || !anyPriced) return all;
      const scoped = this.plans
        .filter((p) => p.prices && p.prices[String(variantId)] !== undefined && p.prices[String(variantId)] !== null)
        .map((p) => Number(p.id));
      return scoped.length > 0 ? scoped : all;
    }

    /*
     * Keep the selected plan legal for the variant. Where the shopper has a real choice of
     * frequency, keep their position in the list, so switching variant doesn't quietly move
     * them from "every 2 months" back to monthly.
     */
    resolvePlanForVariant(variantId) {
      if (!this.plans.length) return;
      const allowed = this.plansForVariant(variantId);
      const current = Number(this.planId);
      if (allowed.includes(current)) return;
      const all = this.plans.map((p) => Number(p.id));
      const currentIndex = all.indexOf(current);
      const next = (currentIndex > -1 && allowed[currentIndex] != null) ? allowed[currentIndex] : allowed[0];
      if (next != null && next !== current) this.planId = next;
    }

    /* Hide frequency options the variant can't take, so the picker never offers one that would fail at add to cart. */
    syncFrequencyOptions() {
      const allowed = this.plansForVariant(this.variantId);
      const showAll = allowed.length === this.plans.length;
      this.root.querySelectorAll('[data-sb-plan]').forEach((el) => {
        const ok = showAll || allowed.includes(Number(el.getAttribute('data-sb-plan')));
        const wrap = el.closest('.sugar-subs__pill-wrap, .sugar-subs__radio-option') || el;
        wrap.hidden = !ok;
        if (el.tagName === 'OPTION') el.disabled = !ok;
      });
    }

    /* === money === */

    formatMoney(cents, whole) {
      // The shop's own format first, so a price built here matches the Liquid one beside it.
      const shopFormat = this.root.dataset.sbMoneyFormat || '';
      if (shopFormat && window.SugarPricing && typeof window.SugarPricing.formatMoney === 'function') {
        return window.SugarPricing.formatMoney(cents, shopFormat, !!whole);
      }
      const amount = whole ? Math.round(cents / 100) : (cents / 100).toFixed(2);
      let out;
      try {
        out = new Intl.NumberFormat(window.Shopify && window.Shopify.locale || 'en-US', {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2
        }).format(amount);
      } catch (e) {
        out = '$' + String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
      return this.showCode ? out + ' ' + this.currency : out;
    }

    discountText(plan, whole) {
      const adj = adjustmentOf(plan);
      if (!adj) return '';
      if (adj.value_type === 'percentage') return adj.value > 0 ? adj.value + '%' : '';
      if ((adj.value_type === 'fixed_amount' && adj.value > 0) || adj.value_type === 'price') {
        return whole ? this.formatMoney(Math.round(adj.value / 100) * 100, true) : this.formatMoney(adj.value);
      }
      return '';
    }

    /* === prices === */

    /* The plan's price for one unit of a variant: Shopify's allocation, else derived from the adjustment */
    planPrice(plan, variantId, base) {
      if (!plan) return base;
      const known = plan.prices && plan.prices[String(variantId)];
      if (known !== undefined && known !== null) return toCents(known);
      return applyAdjustment(base, plan);
    }

    applyOfferToLines(lines) {
      if (!lines.length) return 0;
      const { type, value, scope } = this.offer;
      if (!type || value <= 0) return sumCents(lines);
      /* Per unit, the part of a cent dropped from the discount, the way checkout charges it. */
      if (type === 'percentage') return sumCents(lines.map((p) => p - Math.floor((p * Math.round(value * 100)) / 10000)));
      if (type === 'fixed') {
        if (scope === 'per_item') return sumCents(lines.map((p) => Math.max(0, p - value)));
        return Math.max(0, sumCents(lines) - value);
      }
      return sumCents(lines);
    }

    /* Per-slot variants of the selected offer (its variant picker), else the page variant */
    offerLines() {
      const offer = this.selectedOffer();
      if (!offer) return null;
      const quantity = Math.max(parseInt(offer.dataset.quantity, 10) || this.quantity || 1, 1);
      const state = (offer.closest('.offers') || {}).sugarOffers || null;
      const offerId = String(offer.dataset.blockId || '');
      const fallbackBase = Math.max(0, toCents(offer.dataset.basePrice) || this.variantPrice || 0);
      const compareTotal = toCents(offer.dataset.comparePrice);
      let fallbackCompare = compareTotal > 0 ? Math.round(compareTotal / quantity) : 0;
      if (fallbackCompare <= 0) fallbackCompare = this.variantCompare > 0 ? this.variantCompare : fallbackBase;

      const variantIds = [];
      const baseLines = [];
      const compareLines = [];
      for (let slot = 1; slot <= quantity; slot++) {
        let variantId = String((state && state.selectedVariants && state.selectedVariants[offerId] && state.selectedVariants[offerId][slot]) || '');
        const container = offer.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);
        const select = container && container.querySelector('.offer__variant-select');
        if (!variantId && select) variantId = String(select.value || '');
        if (!variantId && container) {
          const pill = container.querySelector('.offer__variant-pill.is-active');
          if (pill) variantId = String(pill.dataset.variantId || '');
        }
        if (!variantId) variantId = String(offer.dataset.variantId || this.variantId || '');
        let base = fallbackBase;
        let compare = fallbackCompare;
        const option = select && Array.from(select.options).find((o) => String(o.value) === variantId);
        if (option) {
          base = toCents(option.dataset.price) || base;
          compare = toCents(option.dataset.comparePrice) || base;
        } else {
          const known = this.variantById.get(variantId);
          if (known) {
            base = toCents(known.price) || base;
            compare = toCents(known.compare_at_price) || base;
          }
        }
        variantIds.push(variantId);
        baseLines.push(Math.max(0, base));
        compareLines.push(Math.max(0, compare > 0 ? compare : base));
      }
      return { quantity, variantIds, baseLines, compareLines };
    }

    totals() {
      const plan = this.plan;
      const lines = this.offerLines();
      let quantity, variantIds, baseLines, compareLines, onetimeCompare;
      if (lines) {
        ({ quantity, variantIds, baseLines, compareLines } = lines);
        const compareTotal = sumCents(compareLines);
        onetimeCompare = compareTotal > 0 ? compareTotal : sumCents(baseLines);
      } else {
        quantity = Math.max(this.quantity || 1, 1);
        variantIds = Array(quantity).fill(this.variantId);
        baseLines = Array(quantity).fill(Math.max(0, this.variantPrice || 0));
        compareLines = Array(quantity).fill(Math.max(0, this.variantCompare > 0 ? this.variantCompare : this.variantPrice));
        onetimeCompare = sumCents(compareLines);
      }
      const subLines = baseLines.map((base, i) => this.planPrice(plan, variantIds[i], base));
      const onetimePrice = this.applyOfferToLines(baseLines);
      const subscriptionPrice = this.applyOfferToLines(subLines);
      let subscriptionCompare;
      if (this.compareBasis === 'maximum' || this.compareBasis === 'compare_at') {
        const compareTotal = sumCents(compareLines);
        subscriptionCompare = compareTotal > 0 ? compareTotal : onetimeCompare;
      } else if (this.compareBasis === 'before_discounts') {
        subscriptionCompare = sumCents(baseLines);
      } else {
        subscriptionCompare = this.applyOfferToLines(baseLines);
      }
      this.quantity = quantity;
      return {
        quantity,
        onetimePrice: Math.max(0, onetimePrice),
        onetimeCompare: Math.max(0, onetimeCompare),
        subscriptionPrice: Math.max(0, subscriptionPrice),
        subscriptionCompare: Math.max(0, subscriptionCompare)
      };
    }

    /* Everything a template can ask for, for one side of the choice */
    context(type, totals, plan) {
      plan = plan || this.plan;
      const q = Math.max(totals.quantity || 1, 1);
      const price = type === 'subscribe' ? totals.subscriptionPrice : totals.onetimePrice;
      const compare = type === 'subscribe' ? totals.subscriptionCompare : totals.onetimeCompare;
      const saved = compare > price ? compare - price : 0;
      const pct = compare > 0 && saved > 0 ? (saved / compare) * 100 : 0;
      const { number, unit } = frequencyOf(plan);
      return {
        price, compare, saved, quantity: q,
        priceEach: q > 1 ? Math.floor(price / q) : price,
        compareEach: q > 1 ? Math.floor(compare / q) : compare,
        percentage: (Math.round(pct * 10) / 10) + '%',
        percentageRounded: Math.round(pct) + '%',
        amount: this.formatMoney(saved),
        amountRounded: this.formatMoney(Math.round(saved / 100) * 100, true),
        subDiscount: this.discountText(plan),
        subDiscountRounded: this.discountText(plan, true),
        number, unit,
        planName: plan ? String(plan.name || '') : '',
        groupName: this.groupName || (plan ? String(plan.group_name || '') : '')
      };
    }

    fill(template, c) {
      return String(template || '')
        .replace(/\[price_per_(\d+)\]/g, (m, n) => { const d = parseInt(n, 10); return d > 0 ? this.formatMoney(Math.round(c.price / d)) : m; })
        .replace(/\[compare_price\]/g, this.formatMoney(c.compare))
        .replace(/\[price_each\]/g, this.formatMoney(c.priceEach))
        .replace(/\[compare_each\]/g, this.formatMoney(c.compareEach))
        .replace(/\[price\]/g, this.formatMoney(c.price))
        .replace(/\[percentage_rounded\]/g, c.percentageRounded)
        .replace(/\[percentage\]/g, c.percentage)
        .replace(/\[amount_rounded\]/g, c.amountRounded)
        .replace(/\[amount\]/g, c.amount)
        .replace(/\[sub_discount_rounded\]/g, c.subDiscountRounded)
        .replace(/\[sub_discount\]/g, c.subDiscount)
        .replace(/\[number\]/g, c.number)
        .replace(/\[unit\]/g, c.unit)
        .replace(/\[plan_name\]/g, c.planName)
        .replace(/\[group_name\]/g, c.groupName)
        .replace(/\[quantity\]/g, String(c.quantity))
        .replace(/\[br\]/g, '<br>');
    }

    /* === painting === */

    render() {
      const root = this.root;
      const totals = this.totals();
      const ctx = { subscribe: this.context('subscribe', totals), onetime: this.context('onetime', totals) };

      root.querySelectorAll('[data-sb-tpl]').forEach((el) => {
        const c = ctx[el.dataset.sbCtx === 'onetime' ? 'onetime' : 'subscribe'];
        el.innerHTML = this.fill(el.dataset.sbTpl, c);
        if (el.hasAttribute('data-sb-compare')) el.hidden = !(c.saved > 0);
      });

      this.renderOptions(totals);

      root.querySelectorAll('.sugar-subs__option').forEach((option) => {
        const selected = option.dataset.sbType === this.type;
        const was = option.classList.contains('is-selected');
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-checked', String(selected));
        if (!selected) stopTrace(option);
        else if (!was) traceBorder(option);
      });
      root.toggleAttribute('data-sb-locked', this.locked);

      // Child blocks: badges and perks carry their raw text and are filled here
      root.querySelectorAll('[data-badge-template]').forEach((el) => {
        el.innerHTML = this.fill(el.dataset.badgeTemplate, ctx.subscribe);
        el.removeAttribute('data-badge-pending');
        const badge = el.closest('.sugar-deal-badge');
        if (badge) badge.removeAttribute('data-badge-pending');
      });
      root.querySelectorAll('[data-perk-template]').forEach((el) => {
        el.innerHTML = this.fill(el.dataset.perkTemplate, ctx.subscribe);
      });
      if (window.SugarDealBadge) {
        root.querySelectorAll('[data-deal-card]').forEach((card) => window.SugarDealBadge.layoutCard(card, this.autoAlign));
      }
    }

    /* The plan picker: every option's text follows the current variant and quantity */
    renderOptions(totals) {
      const root = this.root;
      const q = Math.max(totals.quantity || 1, 1);
      this.plans.forEach((plan) => {
        const lines = Array(q).fill(this.planPrice(plan, this.variantId, this.variantPrice));
        const planTotals = Object.assign({}, totals, { subscriptionPrice: this.applyOfferToLines(lines) });
        const c = this.context('subscribe', planTotals, plan);
        const label = this.fill(this.optionsFormat, c);
        const discount = this.discountLabel && c.subDiscount ? this.fill(this.discountLabel, c) : '';
        const id = String(plan.id);
        root.querySelectorAll(`.sugar-subs__dropdown option[value="${id}"]`).forEach((opt) => {
          opt.textContent = stripHtml(label) + (discount ? ` (${stripHtml(discount)})` : '');
          opt.selected = Number(plan.id) === Number(this.planId);
        });
        root.querySelectorAll(`.sugar-subs__pill[data-sb-plan="${id}"]`).forEach((pill) => {
          const text = pill.querySelector('.sugar-subs__pill-text');
          if (text) text.innerHTML = label;
          const active = Number(plan.id) === Number(this.planId);
          pill.classList.toggle('is-active', active);
          pill.setAttribute('aria-checked', String(active));
          const wrap = pill.closest('.sugar-subs__pill-wrap') || pill;
          wrap.querySelectorAll('.sugar-subs__discount').forEach((el) => { el.innerHTML = discount; });
        });
        root.querySelectorAll(`.sugar-subs__radio-input[data-sb-plan="${id}"]`).forEach((radio) => {
          radio.checked = Number(plan.id) === Number(this.planId);
          const option = radio.closest('.sugar-subs__radio-option');
          const text = option && option.querySelector('.sugar-subs__radio-text');
          if (text) text.innerHTML = label;
          if (option) option.querySelectorAll('.sugar-subs__discount').forEach((el) => { el.innerHTML = discount; });
        });
      });
    }

    /* === the shared state === */

    publish() {
      const plan = this.type === 'subscribe' ? this.plan : null;
      const detail = {
        sectionId: this.sectionId,
        type: this.type,
        sellingPlan: plan,
        sellingPlanId: plan ? Number(plan.id) : null,
        sellingPlanGroupName: plan ? (this.groupName || plan.group_name || plan.name || '') : '',
        variantPrice: this.variantPrice,
        variantComparePrice: this.variantCompare,
        quantity: this.quantity,
        compareBasis: this.compareBasis
      };
      const state = Object.assign({}, detail);
      delete state.sectionId;
      window.subscriptionStateBySection = window.subscriptionStateBySection || {};
      if (this.sectionId) window.subscriptionStateBySection[this.sectionId] = state;
      window.subscriptionState = state;
      document.dispatchEvent(new CustomEvent('subscription:type-changed', { detail }));
    }

    get state() {
      return { type: this.type, planId: this.planId, plan: this.plan, quantity: this.quantity, locked: this.locked, variantId: this.variantId };
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT).forEach((root) => {
      if (root.sugarSubscriptions) return;
      const controller = new Subscriptions(root);
      root.sugarSubscriptions = controller;
      controller.init();
    });
  }

  window.SugarSubscriptions = { initAll, Subscriptions };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  // The theme editor re-renders a section on a setting change
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    (e.target || document).querySelectorAll(ROOT).forEach((root) => root.sugarSubscriptions && root.sugarSubscriptions.destroy());
  });
})();
